/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {AlertsConfigSearchCriteriaService} from './services/alerts-search-criteria/alerts-config-search-criteria.service';
import {TableHeaderEntryModel} from '../../../shared/table/models/table-header-entry.model';
import {AlertsColumnEnum} from './models/alerts-column.enum';
import alertsConfigMainPageHeaderConfiguration from './configuration/alerts-config-main-page-header.configuration';
import {AlertConfigSummaryModel} from './models/alert-config-summary.model';
import {finalize} from 'rxjs/operators';
import {SpinnerService} from '../../../shared/spinner/spinner.service';
import {AlertConfigQueryContract} from '../../../api/ms-settings/alerts-config/contracts/alert-config-query.contract';
import {AlertsConfigApi} from '../../../api/ms-settings/alerts-config/alerts-config.api';
import {
    Subscription,
    timer
} from 'rxjs';
import {AlertConfigResultContract} from '../../../api/ms-settings/alerts-config/contracts/alert-config-result.contract';
import {SpinnerContextTypeEnum} from '../../../shared/spinner/spinner-context-type.enum';
import {AlertsConfigTableMapper} from './mappers/alerts-config-table.mapper';
import {AbstractFilterStateModel} from '../../../shared/filter/abstract-filter-state.model';
import {InputFilterComponent} from '../../../shared/filter/input-filter/input-filter.component';
import {InputAndCodeFilterComponent} from '../../../shared/filter/input-and-code-filter/input-and-code-filter.component';
import {DateFilterComponent} from '../../../shared/filter/date-filter/date-filter.component';
import {CodeFilterModel} from '../../../shared/filter/code-filter/code-filter.model';
import {AlertsActiveEnum} from './models/alerts-active.enum';
import {TableSortedByModel} from '../../../shared/table/models/table-sorted-by.model';
import {UserService} from '../../../shared/services/user.service';
import {Permission} from '../../../shared/utils/permission.enum';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {AlertsMainPageState} from './models/alerts-main-page.state';
import {TableLineModel} from '../../../shared/table/models/table-line.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {sharedComponentsConfiguration} from '../../../shared/shared-components-configuration';
import {AppService} from '../../../app.service';
import {ResizeObserver} from 'resize-observer';
import {TableAvailableActionsEnum} from '../../../shared/table/models/table-available-actions.enum';
import {TableService} from '../../../shared/table/table-service/table.service';
import {TableLineAvailableActionsModel} from '../../../shared/table/models/table-line-available-actions.model';
import {AlertConfigDetailContract} from '../../../api/ms-settings/alerts-config/contracts/alert-config-detail.contract';
import {ConfirmModalComponent} from '../../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import alertConfigModals from './configuration/alerts-config-modals.configuration';

@Component({
    selector: 'app-alerts-main-page',
    templateUrl: './alerts-main-page.component.html',
    styleUrls: ['./alerts-main-page.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AlertsMainPageComponent extends StatefulComponent<AlertsMainPageState> implements OnInit, OnDestroy, AfterViewInit {
    private subscriptions = new Subscription();
    private readonly ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY = 'ALERTS_CONFIG_LIST_LOADING';
    spinnerObservable = this.spinnerService.onLocalChange(this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);
    triggerAlertsFormSpinner = new EventEmitter<boolean>();
    statusFilterCodes: CodeFilterModel[] = [
        {code: AlertsActiveEnum.ACTIVE, name: 'Active', checked: true, active: true},
        {code: AlertsActiveEnum.INACTIVE, name: 'Inactive', checked: true, active: true}
    ];
    AlertsColumnEnum = AlertsColumnEnum;
    alertsConfig = alertsConfigMainPageHeaderConfiguration;
    reduceTableSize = new EventEmitter<boolean>();

    @ViewChild('alertNameFilter') alertNameFilter: InputFilterComponent;
    @ViewChild('alertNameFilterTemplate', {static: true}) alertNameFilterTemplateRef: TemplateRef<InputFilterComponent> =
            <TemplateRef<InputFilterComponent>>{};
    @ViewChild('alertContentFilter') alertContentFilter: InputFilterComponent;
    @ViewChild('alertContentFilterTemplate', {static: true}) alertContentFilterTemplateRef: TemplateRef<InputFilterComponent> =
            <TemplateRef<InputFilterComponent>>{};
    @ViewChild('startsOnFilter') startsOnFilter: DateFilterComponent;
    @ViewChild('startsOnFilterTemplate', {static: true}) startsOnFilterTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('endsOnFilter') endsOnFilter: DateFilterComponent;
    @ViewChild('endsOnFilterTemplate', {static: true}) endsOnFilterTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('createdByFilter') createdByFilter: InputAndCodeFilterComponent;
    @ViewChild('createdByFilterTemplate', {static: true}) createdByFilterTemplateRef: TemplateRef<InputAndCodeFilterComponent> =
            <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('createdOnFilter') createdOnFilter: DateFilterComponent;
    @ViewChild('createdOnFilterTemplate', {static: true}) createdOnFilterTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('updatedByFilter') updatedByFilter: InputAndCodeFilterComponent;
    @ViewChild('updatedByFilterTemplate', {static: true}) updatedByFilterTemplateRef: TemplateRef<InputAndCodeFilterComponent> =
            <TemplateRef<InputAndCodeFilterComponent>>{};
    @ViewChild('updatedOnFilter') updatedOnFilter: DateFilterComponent;
    @ViewChild('updatedOnFilterTemplate', {static: true}) updatedOnFilterTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('statusFilter') statusFilter: DateFilterComponent;
    @ViewChild('statusFilterTemplate', {static: true}) statusFilterTemplateRef: TemplateRef<DateFilterComponent> =
            <TemplateRef<DateFilterComponent>>{};
    @ViewChild('alertsTable', {static: false}) alertsTable: ElementRef;

    constructor(public search: AlertsConfigSearchCriteriaService,
                private spinnerService: SpinnerService,
                private alertsConfigApi: AlertsConfigApi,
                private userService: UserService,
                private modalService: NgbModal,
                private appService: AppService,
                private tableService: TableService<AlertsColumnEnum, AlertConfigSummaryModel>,
                private cd: ChangeDetectorRef,
                private toast: ToastWrapperService) {
        super();
        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, AlertsMainPageComponent);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    get hasClearAllButton(): boolean {
        return (this.alertNameFilter?.isFilterActive ?? false)
                || (this.alertContentFilter?.isFilterActive ?? false)
                || (this.startsOnFilter?.isFilterActive ?? false)
                || (this.endsOnFilter?.isFilterActive ?? false)
                || (this.createdByFilter?.isFilterActive ?? false)
                || (this.createdOnFilter?.isFilterActive ?? false)
                || (this.updatedByFilter?.isFilterActive ?? false)
                || (this.updatedOnFilter?.isFilterActive ?? false)
                || (this.statusFilter?.isFilterActive ?? false);
    }

    ngOnInit(): void {
        this.storeState({
            isAlertsFormOpen: false,
            openAlertsFormInViewMode: true,
            tableData: {
                tableLineAvailableActions: this.getAvailableActions(),
                expandableLines: false,
                headers: {
                    entries: new Map<AlertsColumnEnum, TableHeaderEntryModel<AlertsColumnEnum>>([
                        [AlertsColumnEnum.NAME, alertsConfigMainPageHeaderConfiguration.alertName],
                        [AlertsColumnEnum.DESCRIPTION, alertsConfigMainPageHeaderConfiguration.alertContent],
                        [AlertsColumnEnum.STARTS_ON, alertsConfigMainPageHeaderConfiguration.startsOn],
                        [AlertsColumnEnum.ENDS_ON, alertsConfigMainPageHeaderConfiguration.endsOn],
                        [AlertsColumnEnum.CREATED_BY, alertsConfigMainPageHeaderConfiguration.createdBy],
                        [AlertsColumnEnum.CREATED_ON, alertsConfigMainPageHeaderConfiguration.createdOn],
                        [AlertsColumnEnum.UPDATED_BY, alertsConfigMainPageHeaderConfiguration.updatedBy],
                        [AlertsColumnEnum.UPDATED_ON, alertsConfigMainPageHeaderConfiguration.updatedOn],
                        [AlertsColumnEnum.ACTIVE, alertsConfigMainPageHeaderConfiguration.status]
                    ])
                },
                staticSection: []
            },
            hasEditPermission: this.userService.hasPermissions([Permission.ADMIN_ALERTS_CONFIG_EDIT])
        });
        this.updateTableFiltersComponents();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.appService.emitShowBackgroundBlur(false);
    }

    ngAfterViewInit(): void {
        const resizeObserver = new ResizeObserver((entries) => {
            const currentWidth = entries[0].contentRect.width;
            if (currentWidth) {
                currentWidth < alertsConfigMainPageHeaderConfiguration.smallTableWidth
                        ? this.reduceTableSize.emit(true)
                        : this.reduceTableSize.emit(false);
            }
        });
        resizeObserver.observe(this.alertsTable.nativeElement);

        this.tableService.getAvailableActionValue()
                .subscribe((value: TableLineAvailableActionsModel) => {
                    switch (value.action) {
                        case TableAvailableActionsEnum.EDIT:
                        case TableAvailableActionsEnum.VIEW:
                            this.getAlertConfigDetail(value.identifier, value.action);
                            break;
                        case TableAvailableActionsEnum.DELETE:
                            this.deleteAlertConfigModal(value.identifier, false);
                            break;
                        default:
                            break;
                    }
                });
    }

    highLightLine(identifier: string, highLightState: boolean): void {
        const line = this.state.tableData.staticSection?.find((item) => item.identifier === identifier);
        if (line != null) {
            line.isLineHighlight = highLightState;
        }
    }

    getAvailableActions(): TableAvailableActionsEnum[] {
        const availableActions: TableAvailableActionsEnum[] = [];
        if (this.userService.hasPermissions([Permission.ADMIN_ALERTS_CONFIG_EDIT])) {
            availableActions.push(TableAvailableActionsEnum.EDIT);
        }
        if (this.userService.hasPermissions([Permission.ADMIN_ALERTS_CONFIG_DELETE])) {
            availableActions.push(TableAvailableActionsEnum.DELETE);
        }
        if (this.userService.hasPermissions([Permission.ADMIN_ALERTS_CONFIG_VIEW])) {
            availableActions.push(TableAvailableActionsEnum.VIEW);
        }
        return availableActions;
    }

    hasAlertConfigCreatePermission(): boolean {
        return this.userService.hasPermissions([Permission.ADMIN_ALERTS_CONFIG_CREATE]);
    }

    changePage = (pageNumber: number): void => {
        this.search.pageNumber = pageNumber;
        this.retrieveTableData(true);
    };

    filterChange(state: AbstractFilterStateModel): void {
        this.search.setState(state.filterName as AlertsColumnEnum, state, () => {
            this.retrieveTableData();
        });
    }

    clearAllFilters(): void {
        this.alertNameFilter?.resetFilter();
        this.alertContentFilter?.resetFilter();
        this.startsOnFilter?.resetFilter();
        this.endsOnFilter?.resetFilter();
        this.createdByFilter?.resetFilter();
        this.createdOnFilter?.resetFilter();
        this.updatedByFilter?.resetFilter();
        this.updatedOnFilter?.resetFilter();
        this.statusFilter?.resetFilter();
        this.search.stateMap.clear();
        this.changePage(1);
    }

    sortedByChange(sortedBy: TableSortedByModel<AlertsColumnEnum>): void {
        this.search.sortedBy = sortedBy.code;
        this.search.sortDirection = sortedBy.direction;
        this.retrieveTableData(true);
    }

    hasPagination(): boolean {
        return this.search.totalResults > this.search.pageSize;
    }

    openCreateAlertConfigForm(action: TableAvailableActionsEnum): void {
        this.appService.emitShowBackgroundBlur(true);
        this.storeState({
            isAlertsFormOpen: !this.state.isAlertsFormOpen,
            openAlertsFormInViewMode: action === TableAvailableActionsEnum.VIEW
        });
    }

    closeForm(close: boolean): void {
        this.highLightLine(this.state.editAlertConfig?.code, false);
        this.appService.emitShowBackgroundBlur(close);
        this.state.isAlertsFormOpen = close;
        this.state.editAlertConfig = null;
    }

    lineToAnimated(identifier: string): void {
        this.retrieveTableData(true, identifier, true);
        this.appService.emitAlertConfigUpdateData(true);
    }

    deleteAlertConfigModal(identifier: string, closeForm: boolean): void {
        const deleteModalConfiguration: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        const currentAlert = this.state.tableData.staticSection?.find((item) => item.identifier === identifier);
        const alertName = currentAlert?.data.get(AlertsColumnEnum.NAME).cellValue;
        const alertStatus = currentAlert?.data.get(AlertsColumnEnum.ACTIVE).cellValue ? 'Active' : 'Inactive';
        const checkOption = `The alert <strong>${alertName}</strong> that is currently ${alertStatus}`
                + ` will be deleted. Be aware that whether the alert is active or inactive, it will never be retrieved.`;

        alertConfigModals.deleteAlertConfig.body.manualChecks.options = [];
        alertConfigModals.deleteAlertConfig.body.manualChecks.options.push(checkOption);

        deleteModalConfiguration.modalConfiguration = {
            ...alertConfigModals.deleteAlertConfig
        };

        deleteModalConfiguration.primaryEvent.subscribe(() => {
            closeForm ? this.deleteAlertConfigFromAlertsForm(identifier, alertName) : this.deleteAlertConfig(identifier, alertName);
        });
    }

    private deleteAlertConfigFromAlertsForm(identifier: string, alertName: string): void {
        this.triggerAlertsFormSpinner.emit(true);

        this.subscriptions.add(
                this.alertsConfigApi.deleteAlertConfig(identifier)
                        .pipe(finalize(() => {
                            this.triggerAlertsFormSpinner.emit(false);
                            this.closeForm(false);
                        }))
                        .subscribe(() => {
                            this.deleteAlertWithSuccessActions(alertName);
                        })
        );
    }

    private deleteAlertConfig(identifier: string, alertName: string): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);

        this.subscriptions.add(
                this.alertsConfigApi.deleteAlertConfig(identifier)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe(() => {
                            this.deleteAlertWithSuccessActions(alertName);
                        })
        );
    }

    private deleteAlertWithSuccessActions(alertName: string): void {
        this.toast.success(`The Alert <strong>${alertName}</strong> was successfully deleted!`,
                'Success');
        this.retrieveTableData(true);
        this.appService.emitAlertConfigUpdateData(true);
    }

    private retrieveTableData(hasPaginationCriteria?: boolean, identifier?: string, animated?: boolean): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        const query: AlertConfigQueryContract = this.search.getAlertsConfigQuery(hasPaginationCriteria);

        this.subscriptions.add(
                this.alertsConfigApi.getAlertsConfig(query)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, AlertsMainPageComponent);
                        }))
                        .subscribe((alerts: AlertConfigResultContract) => {
                            this.storeState({currentAlertsData: alerts.alertsConfigPage});
                            this.search.totalResults = alerts.totalResults;
                            this.state.tableData.staticSection = AlertsConfigTableMapper.fromAlertConfigSummaryContractArray(
                                    this.state.currentAlertsData);

                            if (animated && identifier != null) {
                                const line = this.state.tableData?.staticSection?.find((item) => item.identifier === identifier);
                                this.updateLineAnimation(line, true);

                                timer(sharedComponentsConfiguration.animationTimeout)
                                        .subscribe(() => {
                                            this.updateLineAnimation(line, false);
                                        });
                            }
                        })
        );
        this.cd.detectChanges();
    }

    private updateLineAnimation(line: TableLineModel<AlertsColumnEnum, AlertConfigSummaryModel>,
                                isAnimated: boolean): void {
        if (line != null) {
            line.isAnimated = isAnimated;
        }
    }

    private updateTableFiltersComponents(): void {
        this.state.tableData.headers.entries.get(AlertsColumnEnum.NAME).filterType = this.alertNameFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.DESCRIPTION).filterType = this.alertContentFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.STARTS_ON).filterType = this.startsOnFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.ENDS_ON).filterType = this.endsOnFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.CREATED_BY).filterType = this.createdByFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.CREATED_ON).filterType = this.createdOnFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.UPDATED_BY).filterType = this.updatedByFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.UPDATED_ON).filterType = this.updatedOnFilterTemplateRef;
        this.state.tableData.headers.entries.get(AlertsColumnEnum.ACTIVE).filterType = this.statusFilterTemplateRef;
    }

    private getAlertConfigDetail(alertConfigCode: string, action: TableAvailableActionsEnum): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);
        this.alertsConfigApi.getAlertConfigDetail(alertConfigCode)
                .pipe(finalize(() => {
                    this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.ALERTS_TABLE_LOCAL_LOADING_INSTANCE_KEY);
                }))
                .subscribe((alertConfig: AlertConfigDetailContract) => {
                    if (alertConfig != null) {
                        this.storeState({
                            editAlertConfig: {
                                code: alertConfigCode,
                                editAlertConfigData: AlertsConfigTableMapper
                                        .fromAlertConfigDetailContractToEditAlertConfigModel(alertConfig)
                            }
                        });
                        this.highLightLine(alertConfigCode, true);
                        this.openCreateAlertConfigForm(action);
                    }
                });
    }
}
